// Catálogo geográfico de una obra: país -> región (subdivisión ADM1) -> ubicación.
// "ADM1" es el nombre técnico del primer nivel administrativo de cada país; en la ficha se
// llama por su nombre real (comunidad autónoma, provincia, estado...) o "Región" si el país
// mezcla varios tipos. Lógica pura (sin React) para poder probarla.

// Tipo de subdivisión del catálogo (en inglés, de Natural Earth) -> nombre en español.
export const REGION_TYPE_LABELS = {
    "autonomous community": "Comunidad autónoma",
    "autonomous city": "Ciudad autónoma",
    "autonomous province": "Provincia autónoma",
    "autonomous region": "Región autónoma",
    "autonomous republic": "República autónoma",
    "administrative county": "Condado",
    "canton": "Cantón",
    "capital district": "Distrito capital",
    "capital region": "Región capital",
    "city": "Ciudad",
    "county": "Condado",
    "department": "Departamento",
    "departamento": "Departamento",
    "district": "Distrito",
    "emirate": "Emirato",
    "federal district": "Distrito federal",
    "governorate": "Gobernación",
    "metropolitan department": "Departamento",
    "metropolitan city": "Ciudad metropolitana",
    "municipality": "Municipio",
    "overseas department": "Departamento de ultramar",
    "parish": "Parroquia",
    "prefecture": "Prefectura",
    "province": "Provincia",
    "region": "Región",
    "special municipality": "Municipio",
    "state": "Estado",
    "territory": "Territorio",
    "union territory": "Territorio de la Unión",
    "voivodeship|province": "Voivodato",
};

export const DEFAULT_REGION_LABEL = "Región";
// Nombre que da el catálogo a las ubicaciones aún sin región asignada.
export const UNCLASSIFIED_REGION = "Sin clasificar";

const normalize = (value) => String(value ?? "").trim().toLowerCase();

// Cómo llamar al nivel región en este país: el tipo más repetido de sus subdivisiones si
// cubre la mayoría (España -> "Comunidad autónoma", EE. UU. -> "Estado"). Un país que mezcla
// muchos tipos, como Reino Unido, o cuyos tipos no conocemos, se queda en "Región".
export function regionTypeLabel(subdivisions = []) {
    const counts = new Map();
    let total = 0;
    subdivisions.forEach((subdivision) => {
        const tipo = normalize(subdivision?.tipo);
        // "Sin clasificar" es el cajón del catálogo para ubicaciones sin región: no cuenta.
        if (!tipo || tipo === normalize(UNCLASSIFIED_REGION)) return;
        total += 1;
        const label = REGION_TYPE_LABELS[tipo];
        if (label) counts.set(label, (counts.get(label) || 0) + 1);
    });
    if (!total || !counts.size) return DEFAULT_REGION_LABEL;
    const [label, count] = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "es"))[0];
    return count / total >= 0.6 ? label : DEFAULT_REGION_LABEL;
}

// Ubicaciones visibles: del país, de la región elegida (si hay), activas o deshabilitadas,
// y que coincidan con la búsqueda por nombre o región. `matches` normaliza como el buscador.
export function filterUbicaciones(locations = [], { codPais, codSubdivision = "", showInactive = false, search = "", matches } = {}) {
    const text = String(search ?? "").trim();
    const hit = (value) => !text || (matches ? matches(value, text) : normalize(value).includes(normalize(text)));
    return locations.filter((location) => String(location.codPais) === String(codPais)
        && (!codSubdivision || String(location.codSubdivision) === String(codSubdivision))
        && (showInactive ? !location.active : location.active)
        && (hit(location.value) || hit(location.subdivision)));
}

// Error al crear o editar una ubicación ("" si es válida): sin nombre, sin región o
// repetida dentro de la misma región (el API también lo rechaza con 409).
export function ubicacionError(locations = [], { id = null, nombre, codSubdivision }, keyOf = normalize) {
    const name = String(nombre ?? "").trim();
    if (!codSubdivision) return "Elige la región.";
    if (!name) return "Escribe el nombre de la ubicación.";
    if (name.length > 120) return "Máximo 120 caracteres.";
    const clash = locations.find((location) => String(location.id) !== String(id)
        && String(location.codSubdivision) === String(codSubdivision)
        && keyOf(location.value) === keyOf(name));
    return clash ? `Ya existe «${clash.value}» en esa región.` : "";
}

// País y ubicación que se envían al guardar una obra, resueltos contra el catálogo
// (el API los busca por código). Si el nombre ya no está en el catálogo (renombrado o
// borrado mientras había un cambio pendiente) se avisa: antes se enviaban vacíos y el
// API quitaba el dato sin decir nada. `catalogs`: { pais: [{id, value}], ciudad: [{id, value, parent}] }.
export function geoPayload(obra, catalogs) {
    const country = (catalogs.pais || []).find((entry) => entry.value === obra.pais);
    const city = country && (catalogs.ciudad || []).find((entry) => entry.value === obra.ciudad && entry.parent === obra.pais);
    if (obra.pais && !country) throw new Error(`El país «${obra.pais}» ya no está en el catálogo: elígelo de nuevo en la ficha.`);
    if (obra.ciudad && !country) throw new Error("Selecciona un país antes de asignar una ubicación.");
    if (obra.ciudad && !city) throw new Error(`La ubicación «${obra.ciudad}» ya no está en el catálogo de ${obra.pais}: elígela de nuevo en la ficha.`);
    return {
        pais: country?.value || null,
        codPais: country?.id || null,
        ciudad: city?.value || null,
        codUbicacion: city?.id || null,
    };
}

// Cambios pendientes tras renombrar una ubicación de `pais`: todos los que la usan pasan
// al nombre nuevo, sea cual sea la ubicación guardada de su obra (un alta aún no tiene
// ninguna); con el viejo, el guardado ya no la encontraría. El país de cada obra es el
// pendiente o, si no hay, el guardado (`savedPaisOf(codObra)`). Un cambio que tras
// renombrar deja el mismo valor ya no es un cambio y se quita.
export function renameCityInDrafts(drafts, { previous, next, pais }, savedPaisOf) {
    const paisOf = (codObra) => Object.values(drafts).find((draft) => draft.codObra === codObra && draft.field === "pais")?.newValue ?? savedPaisOf(codObra);
    const renamed = (value) => (value === previous ? next : value);
    return Object.fromEntries(Object.entries(drafts)
        .map(([key, draft]) => [key, draft.field === "ciudad" && paisOf(draft.codObra) === pais
            ? { ...draft, newValue: renamed(draft.newValue), previousValue: renamed(draft.previousValue) }
            : draft])
        .filter(([, draft]) => draft.field !== "ciudad" || String(draft.newValue ?? "") !== String(draft.previousValue ?? "")));
}
